import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, FileUp, Loader2, XCircle } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Link } from 'react-router';
import { errorMessage, get, uploadFile } from '../api';
import { useMe, usePermissions } from '../auth';
import { PageHeader, Progress, StatusBadge } from '../components/ui';
import { LiveIndicator } from '../components/Layout';
import { fmtBytes, fmtNumber } from '../format';
import type { Group, Job } from '../types';
import { JobProgress } from './Jobs';
import { useGroups } from './shared';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp,.bmp,.gif,application/pdf,image/*';

interface Item {
  key: string;
  file: File;
  progress: number;
  jobId?: string;
  error?: string;
}

export function UploadPage() {
  const { group } = useMe();
  const { isSuper } = usePermissions();
  const groups = useGroups(isSuper);
  // El superadmin no pertenece a un grupo: elige a cuál se asignan los documentos.
  const [targetGroup, setTargetGroup] = useState<string>('');
  const groupId = isSuper ? (targetGroup ? Number(targetGroup) : undefined) : group?.id;
  useEffect(() => {
    const active = groups.data?.filter((g) => g.active) ?? [];
    if (isSuper && !targetGroup && active.length === 1) setTargetGroup(String(active[0].id));
  }, [isSuper, targetGroup, groups.data]);
  const [items, setItems] = useState<Item[]>([]);
  const [mode, setMode] = useState<'skip-text' | 'force'>('skip-text');
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const groupInfo = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => get<Group>(`/groups/${groupId}`),
    enabled: !!groupId,
  });

  const patch = (key: string, p: Partial<Item>) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...p } : i)));

  const addFiles = async (files: FileList | File[]) => {
    if (!groupId) return;
    const added = Array.from(files).map((file) => ({ key: `${Date.now()}-${Math.random()}-${file.name}`, file, progress: 0 }));
    setItems((list) => [...added, ...list]);
    // De dos en dos para no saturar la conexión.
    const queue = [...added];
    const worker = async () => {
      for (let item = queue.shift(); item; item = queue.shift()) {
        try {
          const job = await uploadFile(item.file, mode, (progress) => patch(item!.key, { progress }), isSuper ? groupId : undefined);
          patch(item.key, { progress: 1, jobId: job.id });
        } catch (err) {
          patch(item.key, { error: errorMessage(err) });
        }
      }
    };
    await Promise.all([worker(), worker()]);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (e.dataTransfer.files.length) void addFiles(e.dataTransfer.files);
  };

  const quota = groupInfo.data?.monthlyPageQuota;
  const used = groupInfo.data?.pagesUsedThisMonth ?? 0;

  return (
    <>
      <PageHeader
        title="Enviar documentos"
        subtitle="PDF escaneados o imágenes. Se devuelve un PDF con texto seleccionable."
        actions={<LiveIndicator />}
      />

      {isSuper && (
        <div className="field" style={{ maxWidth: 360 }}>
          <label htmlFor="up-group">Grupo destino</label>
          <select id="up-group" className="select" value={targetGroup} onChange={(e) => setTargetGroup(e.target.value)}>
            <option value="">Elige un grupo</option>
            {groups.data
              ?.filter((g) => g.active)
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
          </select>
          <span className="hint">Los documentos y su consumo quedan asignados a este grupo.</span>
        </div>
      )}

      {quota != null && (
        <div className={`alert ${used >= quota ? 'alert-error' : 'alert-info'}`}>
          {used >= quota
            ? `El grupo ha alcanzado su cuota mensual (${fmtNumber(quota)} páginas). Los nuevos envíos se rechazarán.`
            : `Cuota del grupo este mes: ${fmtNumber(used)} de ${fmtNumber(quota)} páginas.`}
        </div>
      )}

      <div
        className={`dropzone ${over ? 'over' : ''} ${groupId ? '' : 'disabled'}`}
        aria-disabled={!groupId}
        onClick={() => groupId && input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (groupId) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => groupId && (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
      >
        <FileUp size={36} strokeWidth={1.6} />
        <h3>{groupId ? 'Arrastra aquí los archivos o haz clic para elegirlos' : 'Elige primero el grupo destino'}</h3>
        <p>PDF, JPEG, PNG, TIFF (varias páginas), WebP, BMP o GIF · cada archivo genera un PDF</p>
        <input
          ref={input}
          type="file"
          multiple
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            if (e.target.files?.length) void addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <label className="checkbox" style={{ marginTop: 14 }}>
        <input type="checkbox" checked={mode === 'force'} onChange={(e) => setMode(e.target.checked ? 'force' : 'skip-text')} />
        <span>
          Hacer OCR también de las páginas que ya tienen texto
          <span className="faint"> — normalmente no hace falta y cuesta más</span>
        </span>
      </label>

      {items.length > 0 && (
        <div className="card upload-list">
          {items.map((item) => (
            <UploadRow key={item.key} item={item} />
          ))}
        </div>
      )}
    </>
  );
}

function UploadRow({ item }: { item: Item }) {
  const job = useQuery({
    queryKey: ['job', item.jobId],
    queryFn: () => get<Job>(`/jobs/${item.jobId}`),
    enabled: !!item.jobId,
  });

  return (
    <div className="upload-item">
      <div style={{ minWidth: 0 }}>
        <div className="cell-main" title={item.file.name}>
          {item.file.name}
        </div>
        <div className="cell-sub">{fmtBytes(item.file.size)}</div>
      </div>
      <div>
        {item.error ? (
          <span style={{ color: 'var(--danger)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <XCircle size={15} /> {item.error}
          </span>
        ) : !item.jobId ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 size={14} className="spin" />
            <Progress value={item.progress} />
          </div>
        ) : job.data ? (
          <JobProgress job={job.data} />
        ) : (
          <CheckCircle2 size={16} />
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        {job.data && (
          <Link to={`/jobs?job=${job.data.id}`}>
            <StatusBadge status={job.data.status} />
          </Link>
        )}
      </div>
    </div>
  );
}
